import type { CellContext, Row } from '@tanstack/react-table';
import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { CustomRouteComponentProps } from 'react-router-dom';
import { compose } from 'redux';

import type { PurchaseOrderTableVM } from 'ab-viewModels/purchaseOrder/purchaseOrderTable.viewModel';

import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { PurchaseOrderStatus, PurchaseOrderStatusLabel } from '@acceligentllc/shared/enums/purchaseOrderStatus';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';
import { TimePeriodRecurrence } from '@acceligentllc/shared/enums/timePeriodRecurrence';

import type { RootState } from 'af-reducers';

import { downloadCSV } from 'af-utils/csv.utils';
import * as SettingsUtils from 'af-utils/settings.util';

import BrowserStorageEnum from 'ab-enums/browserStorage.enum';
import TableButtonType from 'ab-enums/tableButtonType.enum';
import TableNameEnum from 'ab-enums/tableName.enum';

import * as SettingsKeys from 'af-constants/settingsKeys';

import type { TableContent } from 'ab-common/dataStructures/tableContent';
import { TableQuery } from 'ab-common/dataStructures/tableQuery';

import Breadcrumbs from 'af-components/Breadcrumbs';
import ConfirmationModal from 'af-components/ConfirmationModal';
import DateFilter from 'af-components/DateFilter';
import DynamicTooltip from 'af-components/DynamicTooltip';
import Label from 'af-components/Label';
import type { TableRef } from 'af-components/Table';
import TableNew from 'af-components/Table';
import ActionsCell from 'af-components/Table/Cells/ActionsCell';
import DollarCell from 'af-components/Table/Cells/DollarCell';
import EmptyCell from 'af-components/Table/Cells/EmptyCell';
import TextCell from 'af-components/Table/Cells/TextCell';
import type { TableProps } from 'af-components/Table/types';

import CLIENT from 'af-routes/client';

import * as PurchaseOrderActions from 'af-actions/purchaseOrders';

import { toCSVData } from './helpers';
import styles from './styles.module.scss';
import TooltipMessage from './tooltipMessage';

interface SettingProps {
	startDate: Date;
	endDate: Date;
	period: TimePeriodRecurrence;
}

type SelectedPurchaseOrderForDelete = {
	id: number;
	orderNumber: string;
};

type Props = ConnectedProps<typeof connector> & SettingProps & CustomRouteComponentProps;

const normalizeDateToDate = (item: string) => {
	return TimeUtils.normalizeDateToDate(item, TimeFormat.FULL_DATE);

};

const BREADCRUMBS = [{ label: 'Purchase Orders' }];

const PAGE_NUMBER_KEY = SettingsKeys.PURCHASE_ORDER_PAGE_NUMBER();
const PAGE_SEARCH_TEXT_KEY = SettingsKeys.PURCHASE_ORDER_SEARCH_TEXT();

const PurchaseOrder: React.FC<Props> = (props) => {

	const {
		companyName,
		fetchTable,
		getPurchaseOrderTableCSV,
		deletePurchaseOrder,
		history,
		location: { state: { orgAlias } },
	} = props;

	const tableRef = React.useRef<TableRef<PurchaseOrderTableVM>>(null);
	const [startDate, setStartDate] = React.useState(TimeUtils.positionDate(props.startDate, 'start', 'day'));
	const [endDate, setEndDate] = React.useState(TimeUtils.positionDate(props.endDate, 'end', 'day'));
	const [period, setPeriod] = React.useState(props.period);
	const [selectedPurchaseOrder, setSelectedPurchaseOrder] = React.useState<Nullable<SelectedPurchaseOrderForDelete>>(null);

	const onPurchaseOrderClick = React.useCallback((row: Row<PurchaseOrderTableVM>) => {
		history.push(CLIENT.COMPANY.PURCHASE_ORDER.PREVIEW(orgAlias, companyName, `${row.original.id}`));
	}, [companyName, history, orgAlias]);

	const onExportPurchaseOrdersClick = React.useCallback(async () => {
		const tableQuery = tableRef.current!.getTableQuery();
		const _startDate = TimeUtils.formatDate(startDate, TimeFormat.DB_DATE_ONLY);
		const _endDate = TimeUtils.formatDate(endDate, TimeFormat.DB_DATE_ONLY);
		const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		const result = await getPurchaseOrderTableCSV(
			tableQuery,
			_startDate,
			_endDate,
			timezone
		);
		const csvData = toCSVData(result);
		downloadCSV(csvData, `${companyName}_purchase_order_${_startDate}-${_endDate}'}.csv`);
	}, [getPurchaseOrderTableCSV, startDate, endDate, companyName]);

	const buttons = React.useMemo(() => [
		{ label: 'Export Purchase Orders', icon: 'ActionDownload', type: TableButtonType.LINK, hasPermission: true, onClick: onExportPurchaseOrdersClick },
		{ label: 'New Purchase Order', type: TableButtonType.PRIMARY, hasPermission: true, onClick: async () => history.push(CLIENT.COMPANY.PURCHASE_ORDER.CREATE(orgAlias, companyName)) },
	], [companyName, history, onExportPurchaseOrdersClick, orgAlias]);

	const filterByDate = React.useCallback((_startDate: Date, _endDate: Date) => {
		tableRef.current?.resetPagination();
		setStartDate(TimeUtils.positionDate(_startDate, 'start', 'day'));
		setEndDate(TimeUtils.positionDate(_endDate, 'end', 'day'));
		SettingsUtils.setItemWithFormatter(
			SettingsKeys.PURCHASE_ORDER_START_DATE(),
			_startDate,
			TimeUtils.formatDate,
			BrowserStorageEnum.LOCAL_STORAGE,
			TimeFormat.FULL_DATE_WITH_DAY_OF_WEEK
		);
		SettingsUtils.setItemWithFormatter(
			SettingsKeys.PURCHASE_ORDER_END_DATE(),
			_endDate,
			TimeUtils.formatDate,
			BrowserStorageEnum.LOCAL_STORAGE,
			TimeFormat.FULL_DATE_WITH_DAY_OF_WEEK
		);
	}, []);

	const changePeriod = React.useCallback((_period: TimePeriodRecurrence, _selected: Date) => {
		let _startDate: Date = _selected, _endDate: Date = _selected;

		switch (_period) {
			case TimePeriodRecurrence.MONTHLY:
				_startDate = TimeUtils.positionDate(
					TimeUtils.positionDate(_selected, 'start', 'month'),
					'start',
					'day'
				);
				_endDate = TimeUtils.positionDate(
					TimeUtils.positionDate(_selected, 'end', 'month'),
					'end',
					'day'
				);
				break;
			case TimePeriodRecurrence.WEEKLY:
				_startDate = TimeUtils.positionDate(
					TimeUtils.positionDate(_selected, 'start', 'week'),
					'start',
					'day'
				);
				_endDate = TimeUtils.positionDate(
					TimeUtils.positionDate(_selected, 'end', 'week'),
					'end',
					'day'
				);
				break;
			case TimePeriodRecurrence.DAILY:
			case TimePeriodRecurrence.CUSTOM:
				_startDate = TimeUtils.positionDate(_selected, 'start', 'day');
				_endDate = TimeUtils.positionDate(_selected, 'end', 'day');
			default:
				break;
		}

		setStartDate(_startDate);
		setEndDate(_endDate);
		setPeriod(_period);
		tableRef.current?.resetPagination();
		SettingsUtils.setItem(SettingsKeys.PURCHASE_ORDER_PERIOD(), _period, BrowserStorageEnum.LOCAL_STORAGE);
		SettingsUtils.setItemWithFormatter(
			SettingsKeys.PURCHASE_ORDER_START_DATE(),
			_startDate, TimeUtils.formatDate,
			BrowserStorageEnum.LOCAL_STORAGE,
			TimeFormat.FULL_DATE_WITH_DAY_OF_WEEK
		);
		SettingsUtils.setItemWithFormatter(
			SettingsKeys.PURCHASE_ORDER_END_DATE(),
			_endDate,
			TimeUtils.formatDate,
			BrowserStorageEnum.LOCAL_STORAGE,
			TimeFormat.FULL_DATE_WITH_DAY_OF_WEEK
		);
	}, []);

	const renderAdditionalFilter = React.useCallback(() => {
		return (
			<DateFilter
				changePeriod={changePeriod}
				endDate={endDate}
				onChange={filterByDate}
				period={period}
				startDate={startDate}
			/>
		);
	}, [changePeriod, endDate, filterByDate, period, startDate]);

	const renderTextCell = React.useCallback(() => (cell: CellContext<PurchaseOrderTableVM, string>) => {
		return <TextCell value={cell.getValue()} />;
	}, []);

	const renderStatusCell = React.useCallback(() => (cell: CellContext<PurchaseOrderTableVM, PurchaseOrderStatus>) => {
		const value = PurchaseOrderStatusLabel[cell.getValue()];
		const classname = `${cell.getValue().toLowerCase()}`;

		const label = <Label className={classname} text={value} />;

		const tooltipMessage = <TooltipMessage cell={cell} />;

		return (
			<div className={styles['order-table__status']}>
				<DynamicTooltip
					tooltipContent={tooltipMessage}
				>
					{
						label
					}
				</DynamicTooltip>
			</div>);
	}, []);

	const fetchRows = React.useCallback(async (tableRequestModel: TableQuery): Promise<TableContent<PurchaseOrderTableVM>> => {
		const { page, pageSize, filterByText, sortBy } = tableRequestModel;
		const result = await fetchTable(
			new TableQuery({ page, pageSize, sortBy, filterByText }),
			TimeUtils.formatDate(startDate, TimeFormat.DB_DATE_ONLY),
			TimeUtils.formatDate(endDate, TimeFormat.DB_DATE_ONLY),
			Intl.DateTimeFormat().resolvedOptions().timeZone
		);
		return result;
	}, [fetchTable, startDate, endDate]);

	const openPrintPreview = React.useCallback((purchaseOrderId: number, initiatePrint: boolean, fullPrint?: boolean) => {
		const url = new URL(CLIENT.COMPANY.PURCHASE_ORDER.PREVIEW(orgAlias, companyName, `${purchaseOrderId}`), location.origin);
		if (initiatePrint) {
			url.searchParams.append('initiatePrint', 'true');

			if (fullPrint) {
				url.searchParams.append('fullPrint', 'true');
			}
		}

		window.open(url.toString(), '_blank');
	}, [companyName, orgAlias]);

	const openEdit = React.useCallback((purchaseOrderId: number) => {
		const url = new URL(CLIENT.COMPANY.PURCHASE_ORDER.EDIT(orgAlias, companyName, `${purchaseOrderId}`), location.origin);

		window.open(url.toString(), '_blank');
	}, [companyName, orgAlias]);

	const openDelete = React.useCallback((purchaseOrderId: number, orderNumber: string) => {
		setSelectedPurchaseOrder({ id: purchaseOrderId, orderNumber });
	}, []);

	const closeDelete = React.useCallback(() => {
		setSelectedPurchaseOrder(null);
	}, []);

	const deletePO = React.useCallback(async () => {
		if (selectedPurchaseOrder) {
			await deletePurchaseOrder(selectedPurchaseOrder.id);
			closeDelete();
			tableRef.current?.refreshTable();
		}
	}, [selectedPurchaseOrder, deletePurchaseOrder, closeDelete]);

	const resolveActionsButton = React.useCallback((_cell: CellContext<PurchaseOrderTableVM, unknown>) => {
		const actions = [
			{ onClick: () => openPrintPreview(_cell.row.original.id, true, true), label: 'Print Full' },
			{ onClick: () => openPrintPreview(_cell.row.original.id, true), label: 'Print Condensed' },
			{ onClick: () => openPrintPreview(_cell.row.original.id, false), label: 'Preview' },
			{ onClick: () => openEdit(_cell.row.original.id), label: 'Edit' },
		];

		if (_cell.row.original.status === PurchaseOrderStatus.UNFINISHED) {
			actions.push({ onClick: () => openDelete(_cell.row.original.id, _cell.row.original.orderNumber), label: 'Delete' });
		}

		return (
			<ActionsCell
				id="actions"
				isActionDropdown={true}
				labelKey="label"
				options={actions}
				valueKey="label"
			/>
		);
	}, [openDelete, openEdit, openPrintPreview]);

	const columns: TableProps<PurchaseOrderTableVM>['columns'] = React.useMemo(() => [
		{
			id: 'orderNumber',
			accessor: 'orderNumber',
			header: 'PO Number',
			cell: renderTextCell(),
			enableSorting: true,
			enableHiding: true,
			size: 96,
		},
		{
			id: 'status',
			accessor: 'status',
			header: 'Status',
			cell: renderStatusCell(),
			enableSorting: true,
			enableHiding: true,
			size: 150,
		},
		{
			id: 'dateCreated',
			accessor: 'dateCreated',
			header: 'Date Created',
			cell: (cell: CellContext<PurchaseOrderTableVM, string>) =>
				<TextCell value={cell.getValue() ? TimeUtils.formatDate(cell.getValue(), TimeFormat.DATE_ONLY, TimeFormat.ISO_DATETIME) : 'N/A'} />,
			enableSorting: true,
			enableHiding: true,
			size: 96,
		},
		{
			id: 'dateSubmitted',
			accessor: 'dateSubmitted',
			header: 'Date Submitted',
			cell: (cell: CellContext<PurchaseOrderTableVM, string>) =>
				<TextCell value={cell.getValue() ? TimeUtils.formatDate(cell.getValue(), TimeFormat.DATE_ONLY, TimeFormat.ISO_DATETIME) : 'N/A'} />,
			enableSorting: true,
			enableHiding: true,
			size: 96,
		},
		{
			id: 'vendorName',
			accessor: 'vendorName',
			header: 'Vendor',
			cell: renderTextCell(),
			enableSorting: true,
			enableHiding: true,
			size: 96,
		},
		{
			id: 'locationName',
			accessor: 'locationName',
			header: 'Division',
			cell: renderTextCell(),
			enableSorting: true,
			enableHiding: true,
			size: 96,
		},
		{
			id: 'totalCost',
			accessor: 'totalCost',
			header: 'Total Cost',
			cell: (cell: CellContext<PurchaseOrderTableVM, PurchaseOrderTableVM['totalCost']>) =>
				<DollarCell isUnitPrice={true} maxFractionDigits={2} minFractionDigits={2} value={+cell.getValue()} />,
			enableSorting: true,
			enableHiding: true,
			sortDescFirst: true,
			size: 96,
		},
		{
			id: 'equipmentCode',
			accessor: 'equipmentCode',
			header: 'Equipment Code',
			cell: renderTextCell(),
			enableSorting: true,
			enableHiding: true,
			size: 104,
		},
		{
			id: 'jobCode',
			accessor: 'jobCode',
			header: 'Job Number',
			cell: renderTextCell(),
			enableSorting: true,
			enableHiding: true,
			size: 96,
		},
		{
			id: 'requestedBy',
			accessor: 'requestedBy',
			header: 'Requested by',
			cell: renderTextCell(),
			enableSorting: true,
			enableHiding: true,
			size: 140,
		},
		{
			id: 'submittedBy',
			accessor: 'submittedBy',
			header: 'Submitted by',
			cell: renderTextCell(),
			enableSorting: true,
			enableHiding: true,
			size: 140,
			sortDescFirst: true,
		},
		{
			id: 'itemsReceived',
			accessor: 'itemsReceived',
			header: 'Item Received',
			cell: renderTextCell(),
			enableSorting: true,
			enableHiding: true,
			size: 96,
		},
		{
			id: 'itemsInvoiced',
			accessor: 'itemsInvoiced',
			header: 'Items Invoiced',
			cell: renderTextCell(),
			enableSorting: true,
			enableHiding: true,
			size: 96,
		},
		{
			id: 'actions',
			isDisplayColumn: true,
			header: () => <EmptyCell isHeader />,
			cell: resolveActionsButton,
		},
	], [renderStatusCell, renderTextCell, resolveActionsButton]);

	return (
		<div className="form-segment form-segment--maxi">
			<Breadcrumbs items={BREADCRUMBS} />
			<TableNew
				additionalFilter={renderAdditionalFilter}
				buttons={buttons}
				columns={columns}
				defaultPageSize={100}
				fetch={fetchRows}
				hasSearchInput={true}
				hasSubRows={false}
				offsetHeight={-35}
				onRowClick={onPurchaseOrderClick}
				pageNumberKey={PAGE_NUMBER_KEY}
				ref={tableRef}
				searchTextKey={PAGE_SEARCH_TEXT_KEY}
				selectable={false}
				tableName={TableNameEnum.PURCHASE_ORDER}
			/>
			{
				selectedPurchaseOrder &&
				<ConfirmationModal
					body={`You are about to delete Purchase Order ${selectedPurchaseOrder.orderNumber}.`}
					closeModal={closeDelete}
					closeText="Cancel"
					confirmAction={deletePO}
					confirmText="Delete"
					modalStyle="danger"
					showModal={!!selectedPurchaseOrder}
					size="md"
					title="Delete Purchase Order"
				/>
			}
		</div>
	);
};

function mapStateToProps(state: RootState) {
	const { companyData } = state.user;
	if (!companyData) {
		throw new Error('User not logged in');
	}

	return {
		companyName: companyData.name,
	};
}

function mapDispatchToProps() {
	return {
		getPurchaseOrderTableCSV: PurchaseOrderActions.findCompanyPurchaseOrderTableCSV,
		fetchTable: PurchaseOrderActions.findCompanyPurchaseOrderTable,
		deletePurchaseOrder: PurchaseOrderActions.deletePurchaseOrder,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose(
	SettingsUtils.withSettings<SettingProps>(() => ([
		{
			key: SettingsKeys.PURCHASE_ORDER_START_DATE(),
			mappedName: 'startDate',
			normalize: normalizeDateToDate,
			defaultValue: new Date(),
			source: BrowserStorageEnum.LOCAL_STORAGE,
		},
		{
			key: SettingsKeys.PURCHASE_ORDER_END_DATE(),
			mappedName: 'endDate',
			normalize: normalizeDateToDate,
			defaultValue: new Date(),
			source: BrowserStorageEnum.LOCAL_STORAGE,
		},
		{
			key: SettingsKeys.PURCHASE_ORDER_PERIOD(),
			mappedName: 'period',
			defaultValue: TimePeriodRecurrence.DAILY,
			source: BrowserStorageEnum.LOCAL_STORAGE,
		},
	])),
	connector
);

export default enhance(PurchaseOrder);
